import React, { useState, useEffect } from "react";
import baseUrl from "../../../api/baseUrl";

const statusColors: Record<string, string> = {
  Started: "#F2A91C",
  Ordered: "#F2A91C",
  Preparing: "#F2A91C",
  Ready: "#067904",
  Served: "#3FC091",
  Completed: "#4763e4",
  Cancelled: "#DC1818",
  Pending: "#D7D7D7",
};

const Kot: React.FC = () => {
  const [allTableOrders, setAllTableOrders] = useState<any[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all tables from backend on mount
  useEffect(() => {
    baseUrl
      .get("/branch/table/get-tables")
      .then((res) => setAllTableOrders(res.data.data || []))
      .catch(() => setAllTableOrders([]));
  }, []);

  // Only show tables with status not "Available"
  const kotTables = allTableOrders.filter(
    (table: any) => String(table.status).toLowerCase() !== "available"
  );

  // Fetch current order details for selected table (except Completed)
  useEffect(() => {
    if (!selectedTable) {
      setOrderDetails([]);
      return;
    }
    const tableObj = kotTables.find(
      (t: any) => t.tableNumber?.toString() === selectedTable
    );
    if (!tableObj?.id) {
      setOrderDetails([]);
      return;
    }
    setLoading(true);
    baseUrl
      .get(`/branch/order/table/${tableObj.id}`)
      .then((res) => {
        // Filter out orders with status "Completed"
        const filtered = (res.data.data || []).filter(
          (order: any) => String(order.status).toLowerCase() !== "completed"
        );
        setOrderDetails(filtered);
      })
      .catch((err) => {
        setOrderDetails([]);
        console.error("Error fetching order details:", err);
      })
      .finally(() => setLoading(false));
    // Only depend on selectedTable
    // eslint-disable-next-line
  }, [selectedTable]);

  // Accept order: set order status to "Preparing" and table status to "Ongoing"
  const handleAcceptOrder = async (orderNumber: string, tableId: number) => {
    try {
      await baseUrl.put(`/branch/order/order-number/${orderNumber}/status`, {
        status: "Preparing",
      });
      await baseUrl.patch(`/branch/table/update-table-status/${tableId}`, {
        status: "Ongoing",
      });
      setOrderDetails((prev) =>
        prev.map((o) =>
          o.orderNumber === orderNumber ? { ...o, status: "Preparing" } : o
        )
      );
      baseUrl
        .get("/branch/table/get-tables")
        .then((res) => setAllTableOrders(res.data.data || []));
    } catch (err) {
      alert("Failed to accept order");
    }
  };

  // Finish order: set order status to "Ready"
  const handleFinishOrder = async (orderNumber: string) => {
    try {
      await baseUrl.put(`/branch/order/order-number/${orderNumber}/status`, {
        status: "Ready",
      });
      setOrderDetails((prev) =>
        prev.map((o) =>
          o.orderNumber === orderNumber ? { ...o, status: "Ready" } : o
        )
      );
    } catch (err) {
      alert("Failed to finish order");
    }
  };

  // Collected order: set order status to "Served"
  const handleCollectedOrder = async (orderNumber: string) => {
    try {
      await baseUrl.put(`/branch/order/order-number/${orderNumber}/status`, {
        status: "Served",
      });
      setOrderDetails((prev) =>
        prev.map((o) =>
          o.orderNumber === orderNumber ? { ...o, status: "Served" } : o
        )
      );
    } catch (err) {
      alert("Failed to update order as collected");
    }
  };

  // Not available: set order status to "Cancelled"
  const handleNotAvailable = async (orderNumber: string) => {
    try {
      // Find the order and its tableId
      const order = orderDetails.find(
        (o: any) => o.orderNumber === orderNumber
      );
      await baseUrl.put(`/branch/order/order-number/${orderNumber}/status`, {
        status: "Cancelled",
      });

      // If only one order left (this one), also set table status to "Available"
      if (order && orderDetails.length === 1) {
        await baseUrl.patch(
          `/branch/table/update-table-status/${order.tableId}`,
          {
            status: "Available",
          }
        );
        baseUrl
          .get("/branch/table/get-tables")
          .then((res) => setAllTableOrders(res.data.data || []));
      }

      setOrderDetails((prev) =>
        prev.map((o) =>
          o.orderNumber === orderNumber ? { ...o, status: "Cancelled" } : o
        )
      );
    } catch (err) {
      alert("Failed to update order status");
    }
  };

  if (!kotTables.length) {
    return (
      <div style={{ padding: 32 }}>
        <h2>KOT Tables</h2>
        <div style={{ color: "#888" }}>No KOT data available.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 32 }}>
      <h2>KOT Tables</h2>
      <div style={{ display: "flex", gap: 24 }}>
        {/* Table List */}
        <div style={{ minWidth: 180 }}>
          <h4>Tables</h4>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {kotTables.map((kot: any) => (
              <li key={kot.tableNumber}>
                <button
                  style={{
                    width: "100%",
                    padding: "10px 0",
                    margin: "6px 0",
                    background:
                      selectedTable === kot.tableNumber?.toString()
                        ? "#4763e4"
                        : "#f5f5f7",
                    color:
                      selectedTable === kot.tableNumber?.toString()
                        ? "#fff"
                        : "#222",
                    border: "none",
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedTable(kot.tableNumber?.toString())}
                >
                  Table {kot.tableNumber}
                </button>
              </li>
            ))}
          </ul>
        </div>
        {/* KOT Items for selected table */}
        <div style={{ flex: 1 }}>
          {selectedTable ? (
            loading ? (
              <div style={{ color: "#888" }}>Loading...</div>
            ) : orderDetails.length === 0 ? (
              <div style={{ color: "#888" }}>No order details found.</div>
            ) : (
              <>
                <h4>Orders for Table {selectedTable}</h4>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left" }}>Order #</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetails.map((order: any, idx: number) => (
                      <tr key={order.id || idx}>
                        <td>{order.orderNumber}</td>
                        <td>
                          {order.itemName || order.itemId}
                          {/* Show quantity ordered for this item */}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {order.quantity}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            style={{
                              background:
                                statusColors[order.status] || "#D7D7D7",
                              color: "#fff",
                              padding: "2px 10px",
                              borderRadius: 6,
                              fontWeight: 600,
                            }}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {/* ...existing action buttons... */}
                          {order.status === "Started" && (
                            <>
                              <button
                                style={{
                                  background: "#067904",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  padding: "6px 14px",
                                  fontWeight: 600,
                                  marginRight: 8,
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleAcceptOrder(
                                    order.orderNumber,
                                    order.tableId
                                  )
                                }
                              >
                                Accept
                              </button>
                              <button
                                style={{
                                  background: "#DC1818",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  padding: "6px 14px",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleNotAvailable(order.orderNumber)
                                }
                              >
                                Not Available
                              </button>
                            </>
                          )}
                          {order.status === "Preparing" && (
                            <button
                              style={{
                                background: "#f7941a",
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                padding: "6px 14px",
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleFinishOrder(order.orderNumber)
                              }
                            >
                              Finished
                            </button>
                          )}
                          {order.status === "Ready" && (
                            <button
                              style={{
                                background: "#4763e4",
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                padding: "6px 14px",
                                fontWeight: 600,
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                handleCollectedOrder(order.orderNumber)
                              }
                            >
                              Collected
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )
          ) : (
            <div style={{ color: "#888" }}>
              Select a table to view KOT order details.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Kot;
